import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, UnauthenticatedError } from "../api/client";

export type DevRole = "admin" | "family";

interface AuthMe {
  email: string;
  role: DevRole;
  expiresAt: number;
}

export type AuthStatus =
  | { status: "loading" }
  | { status: "unauthenticated"; login: () => void; logout: () => Promise<void> }
  | {
      status: "authenticated";
      email: string;
      role: DevRole;
      expiresAt: number;
      logout: () => Promise<void>;
    };

export function useAuth(): AuthStatus {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<AuthMe>({
    queryKey: ["auth", "me"],
    queryFn: async () => apiFetch<AuthMe>("/api/dev/auth/me"),
    retry: false,
    staleTime: 60_000,
  });

  async function logout() {
    await fetch("/api/dev/auth/logout", {
      method: "POST",
      credentials: "include",
    });
    queryClient.setQueryData(["auth", "me"], undefined);
    queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
  }

  function login() {
    queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
  }

  if (isLoading) return { status: "loading" };
  if (error instanceof UnauthenticatedError || !data) {
    return { status: "unauthenticated", login, logout };
  }
  return {
    status: "authenticated",
    email: data.email,
    role: data.role ?? "admin",
    expiresAt: data.expiresAt,
    logout,
  };
}
